import { Add } from "@mui/icons-material";
import { styled } from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { RegisteredTable } from "components/RegisteredTable";
import { StyledButton } from "components/StyledButton";
import { DELETE_FAIL } from "messages";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "PeoplePage";

const classes = {
	root: `${PREFIX}-root`,
	appBar: `${PREFIX}-appBar`,
	indicator: `${PREFIX}-indicator`,
	table: `${PREFIX}-table`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.root}`]: {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
	},

	[`& .${classes.appBar}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.table}`]: {
		marginTop: theme.spacing(4),
	},
}));

export const PeoplePage = (): JSX.Element => {
	const [err, setErr] = useState("");
	const [users, setUsers] = useState<types.User[]>([]);

	useEffect(() => {
		api
			.getUsers()
			.then((res) => {
				if (res.status !== 200) {
					setErr(DELETE_FAIL);
					return;
				}

				if (res.data === null) {
					return;
				}

				if (res.data.length === 0) {
					setUsers([]);
					return;
				}

				setUsers(res.data);
			})
			.catch(() => setErr(DELETE_FAIL));
	}, []);

	// Handle delete user, will delete all users in the delete list.
	const onDelete = async (userIds: string[]): Promise<void> => {
		// convert the above into async/await
		try {
			const res = await api.getMyUserData();
			if (res.status !== 200) {
				throw new Error(DELETE_FAIL);
			}

			// Make sure you're not deleting yourself
			if (userIds.includes(res.data.userId)) {
				throw new Error("You can't delete yourself!");
			}
		} catch (e) {
			setErr((e as Error)?.message || DELETE_FAIL);
			return;
		}

		try {
			const res = await api.deleteUsers(userIds);
			if (res.status === 200) {
				setUsers(users.filter((item) => !userIds.includes(item.userId)));
				return;
			}
			if (res.status !== 207) {
				throw new Error(DELETE_FAIL);
			}

			// Partial success: drop the deleted users, keep the ones listed as failed. Users in
			// "warnings" were deleted, but their existing sessions could not be ended.
			const body = JSON.parse(res.data) as {
				failed?: Record<string, string>;
				warnings?: Record<string, string>;
			};
			const failedIds = Object.keys(body.failed ?? {});
			const warningCount = Object.keys(body.warnings ?? {}).length;
			setUsers(
				users.filter(
					(item) => !userIds.includes(item.userId) || failedIds.includes(item.userId),
				),
			);
			const messages = [];
			if (failedIds.length > 0) {
				messages.push(
					`${failedIds.length} of ${userIds.length} users could not be deleted, please try again!`,
				);
			}
			if (warningCount > 0) {
				messages.push(
					`${warningCount} deleted users may stay signed in until their session expires.`,
				);
			}
			setErr(messages.join(" "));
		} catch (e) {
			setErr((e as Error)?.message || DELETE_FAIL);
		}
	};

	return (
		<Root>
			<RegisteredTable users={users} onDelete={onDelete}>
				<StyledButton
					variant="contained"
					color="primary"
					component={Link}
					size="medium"
					to={routes._.people._.createAccounts.path}
					startIcon={<Add />}
					sx={{ minWidth: 200 }}
				>
					Create Accounts
				</StyledButton>
			</RegisteredTable>

			<InfoSnackBar
				isOpen={err !== ""}
				onClose={() => setErr("")}
				severity="error"
				message={err}
			/>
		</Root>
	);
};
